
export {};

const MOBILE_MEDIA = window.matchMedia('(max-width: 1023px)');

/** @type {WeakMap<Element, AbortController>} */
const drawerControllers = new WeakMap();

/**
 * @param {MouseEvent} event
 */
function isModifiedClick(event) {
  return event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;
}

/**
 * @param {Element} root
 */
function getAccordionItems(root) {
  return Array.from(root.querySelectorAll('.menu-list__list-item')).filter((item) => {
    return item.querySelector(':scope > .menu-list__submenu') instanceof HTMLElement;
  });
}

/**
 * Limit submenu expand height to visible viewport.
 * @param {HTMLElement} submenu
 */
function getExpandedHeight(submenu) {
  const rect = submenu.getBoundingClientRect();
  const drawerContainer = submenu.closest('.spx-menu-drawer.menu-drawer, .menu-drawer');
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  const containerBottom =
    drawerContainer instanceof HTMLElement
      ? drawerContainer.getBoundingClientRect().bottom
      : viewportHeight;
  const availableHeight = Math.max(0, Math.floor(containerBottom - rect.top));
  const contentHeight = submenu.scrollHeight;
  return Math.min(contentHeight, availableHeight);
}

/**
 * @param {HTMLElement} drawer
 */
function refreshExpandedSubmenus(drawer) {
  const expandedSubmenus = drawer.querySelectorAll(
    '.spx-menu-accordion-item[data-expanded] > .spx-menu-accordion-submenu'
  );

  expandedSubmenus.forEach((submenu) => {
    if (!(submenu instanceof HTMLElement)) return;
    submenu.style.maxHeight = `${getExpandedHeight(submenu)}px`;
  });
}

/**
 * Recalculate after layout settles (switching menu/images/fonts).
 * @param {HTMLElement} drawer
 */
function queueRecalculateExpandedHeights(drawer) {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      refreshExpandedSubmenus(drawer);
    });
  });

  window.setTimeout(() => {
    if (!MOBILE_MEDIA.matches) return;
    refreshExpandedSubmenus(drawer);
  }, 220);
}

/**
 * @param {HTMLElement} submenu
 * @param {boolean} expanded
 */
function setSubmenuExpanded(submenu, expanded) {
  submenu.classList.add('spx-menu-accordion-submenu');
  submenu.setAttribute('aria-hidden', expanded ? 'false' : 'true');

  if (expanded) {
    submenu.style.maxHeight = '0px';
    requestAnimationFrame(() => {
      submenu.style.maxHeight = `${getExpandedHeight(submenu)}px`;
    });
  } else {
    submenu.style.maxHeight = `${Math.ceil(submenu.getBoundingClientRect().height)}px`;
    requestAnimationFrame(() => {
      submenu.style.maxHeight = '0px';
    });
  }
}

/**
 * @param {HTMLElement} item
 * @param {HTMLAnchorElement | null} link
 * @param {HTMLElement} submenu
 */
function collapseItem(item, link, submenu) {
  item.removeAttribute('data-expanded');
  link?.setAttribute('aria-expanded', 'false');
  setSubmenuExpanded(submenu, false);
}

/**
 * @param {HTMLElement} item
 * @param {HTMLAnchorElement | null} link
 * @param {HTMLElement} submenu
 */
function expandItem(item, link, submenu) {
  item.setAttribute('data-expanded', '');
  link?.setAttribute('aria-expanded', 'true');
  setSubmenuExpanded(submenu, true);
}

/**
 * @param {Element} root
 */
function setupItems(root) {
  const items = getAccordionItems(root);

  items.forEach((item) => {
    if (!(item instanceof HTMLElement)) return;
    item.classList.add('spx-menu-accordion-item');

    const link = item.querySelector(':scope > a.menu-list__link');
    if (link instanceof HTMLAnchorElement) {
      link.setAttribute('aria-expanded', item.hasAttribute('data-expanded') ? 'true' : 'false');
    }

    const submenu = item.querySelector(':scope > .menu-list__submenu');
    if (!(submenu instanceof HTMLElement)) return;

    if (!item.hasAttribute('data-expanded')) {
      setSubmenuExpanded(submenu, false);
    } else {
      setSubmenuExpanded(submenu, true);
    }
  });
}

/**
 * @param {HTMLElement} drawer
 */
function initDrawer(drawer) {
  if (drawerControllers.has(drawer)) return;

  const controller = new AbortController();
  const { signal } = controller;
  drawerControllers.set(drawer, controller);

  setupItems(drawer);

  drawer.addEventListener(
    'click',
    (/** @type {MouseEvent} */ event) => {
      if (!MOBILE_MEDIA.matches) return;
      if (!(event.target instanceof Element)) return;

      const link = event.target.closest('a.menu-list__link');
      if (!(link instanceof HTMLAnchorElement)) return;
      if (!drawer.contains(link)) return;

      const item = link.closest('.menu-list__list-item');
      if (!(item instanceof HTMLElement)) return;

      const submenu = item.querySelector(':scope > .menu-list__submenu');
      if (!(submenu instanceof HTMLElement)) return;

      if (isModifiedClick(event)) return;

      event.preventDefault();
      event.stopPropagation();

      const expanded = item.hasAttribute('data-expanded');
      if (expanded) {
        collapseItem(item, link, submenu);
      } else {
        drawer
          .querySelectorAll('.spx-menu-accordion-item[data-expanded]')
          .forEach((openedItem) => {
            if (!(openedItem instanceof HTMLElement)) return;
            if (openedItem === item) return;

            const openedLink = openedItem.querySelector(':scope > a.menu-list__link');
            const openedSubmenu = openedItem.querySelector(':scope > .menu-list__submenu');
            if (!(openedSubmenu instanceof HTMLElement)) return;
            collapseItem(
              openedItem,
              openedLink instanceof HTMLAnchorElement ? openedLink : null,
              openedSubmenu
            );
          });

        expandItem(item, link, submenu);
        queueRecalculateExpandedHeights(drawer);
      }
    },
    { signal }
  );

  drawer.addEventListener(
    'spx:submenu-content-change',
    () => {
      if (!MOBILE_MEDIA.matches) return;
      queueRecalculateExpandedHeights(drawer);
    },
    { signal }
  );

  window.addEventListener(
    'resize',
    (/** @type {UIEvent} */ _event) => {
      if (!MOBILE_MEDIA.matches) return;
      refreshExpandedSubmenus(drawer);
    },
    { signal }
  );
}

/**
 * @param {HTMLElement} drawer
 */
function destroyDrawer(drawer) {
  const controller = drawerControllers.get(drawer);
  if (controller) controller.abort();
  drawerControllers.delete(drawer);

  drawer.querySelectorAll('.spx-menu-accordion-item').forEach((item) => {
    if (!(item instanceof HTMLElement)) return;
    item.classList.remove('spx-menu-accordion-item');
    item.removeAttribute('data-expanded');

    const link = item.querySelector(':scope > a.menu-list__link');
    if (link instanceof HTMLAnchorElement) {
      link.setAttribute('aria-expanded', 'false');
    }

    const submenu = item.querySelector(':scope > .menu-list__submenu');
    if (submenu instanceof HTMLElement) {
      submenu.classList.remove('spx-menu-accordion-submenu');
      submenu.style.maxHeight = '';
      submenu.removeAttribute('aria-hidden');
    }
  });
}

function initAll() {
  document.querySelectorAll('header-drawer').forEach((drawer) => {
    if (!(drawer instanceof HTMLElement)) return;
    initDrawer(drawer);
  });
}

function teardownAll() {
  document.querySelectorAll('header-drawer').forEach((drawer) => {
    if (!(drawer instanceof HTMLElement)) return;
    if (!drawerControllers.has(drawer)) return;
    destroyDrawer(drawer);
  });
}

function applyMode() {
  if (MOBILE_MEDIA.matches) {
    initAll();
  } else {
    teardownAll();
  }
}

applyMode();
MOBILE_MEDIA.addEventListener('change', applyMode);

const observer = new MutationObserver(() => {
  if (!MOBILE_MEDIA.matches) return;
  initAll();
});

observer.observe(document.documentElement, { subtree: true, childList: true });
